// Text with inline icons for Phaser (spec §1): a Container of Text runs and atlas images laid
// out by iconTextLayout.mjs. It answers the Text methods its callers use, so swapping
// scene.add.text(...) for iconText(scene, ...) keeps working code working.
import Phaser from 'phaser';
import { ICONS, iconKey } from './icons.mjs';
import { layoutIconText, runStyle, hitArea } from './iconTextLayout.mjs';
import { resolveFamily } from './gameFontRule.mjs';

export class IconText extends Phaser.GameObjects.Container {
  constructor(scene, x, y, text, style = {}) {
    super(scene, x, y);
    this._style = { fontSize: '18px', color: '#000000', ...style };
    // scene.make.text skips gameFont's scene.add.text patch, so resolve the family here.
    this._style.fontFamily = resolveFamily(this._style.fontFamily);
    this._origin = { x: 0, y: 0 };
    this._text = '';
    scene.add.existing(this);
    this.setText(text);
  }

  get text() { return this._text; }
  // gameFont.refreshTexts: the font landed — rebuild, don't take the plain fast path (its
  // Text.setText(same) would keep the fallback-font drawing).
  refreshLayout() { this._runStyleKey = null; this._layout(); }   // gameFont.refreshTexts: re-measure once the font lands
  get fontPx() { return parseInt(this._style.fontSize, 10) || 18; }

  // Every setter returns early when nothing changes (as Phaser's Text.setText does): the tool
  // strip refreshes each second and a vehicle's bar every frame with mostly the same values, and
  // a relayout rebuilds every run.
  setText(str) { const v = String(str ?? ''); if (v === this._text && this.list.length) return this; this._text = v; this._layout(); return this; }
  setColor(c) { return this._restyle({ color: c }); }
  setStroke(c, t) { return this._restyle({ stroke: c, strokeThickness: t }); }
  setFontSize(px) { return this._restyle({ fontSize: typeof px === 'number' ? `${px}px` : px }); }
  setWordWrapWidth(w) { if (this._style.wordWrap?.width === w) return this; this._style.wordWrap = { width: w }; this._layout(); return this; }
  setAlign(a) { return this._restyle({ align: a }); }
  setFontStyle(fs) { return this._restyle({ fontStyle: fs }); }
  setFontFamily(f) { return this._restyle({ fontFamily: resolveFamily(f) }); }
  setFill(c) { return this.setColor(c); }
  setBackgroundColor(c) { return this._restyle({ backgroundColor: c }); }
  setPadding(x, y = x) { return this._restyle({ padding: typeof x === 'object' ? x : { x, y } }); }
  setShadow(offsetX = 0, offsetY = 0, color = '#000', blur = 0, stroke = false, fill = true) {
    return this._restyle({ shadow: { offsetX, offsetY, color, blur, stroke, fill } });
  }
  setOrigin(x = 0.5, y = x) { if (this._origin.x === x && this._origin.y === y) return this; this._origin = { x, y }; this._layout(); return this; }

  _restyle(patch) {
    if (Object.entries(patch).every(([k, v]) => this._style[k] === v)) return this;
    Object.assign(this._style, patch);
    this._layout();
    return this;
  }
  // A container child renders with its OWN scroll factor, so pin the runs too (a pinned toast
  // on the scrolling street would otherwise slide away with the camera).
  setScrollFactor(x, y = x) {
    this._sf = [x, y];
    super.setScrollFactor(x, y);
    for (const c of this.list) c.setScrollFactor(x, y);   // not Phaser's updateChildren: see pin.mjs
    return this;
  }
  get displayWidth() { return this.width * this.scaleX; }
  get displayHeight() { return this.height * this.scaleY; }

  _layout() {
    const st = runStyle(this._style);
    const measure = measurer(st);
    const L = layoutIconText(this._text, {
      measure, fontSize: this.fontPx, wrapWidth: this._style.wordWrap?.width ?? 0,
      align: this._style.align ?? 'left', lineSpacing: this._style.lineSpacing ?? 4, known: (n) => n in ICONS,
    });
    // One plain text run and no box, like last time: update that Text in place (a speedometer or
    // timer changing every frame then costs one Text.setText, not a destroy + create).
    const only = L.lines.length === 1 && L.lines[0].runs.length === 1 && L.lines[0].runs[0].type === 'text';
    const prev = this.list.length === 1 && this.list[0].type === 'Text' ? this.list[0] : null;
    const key = JSON.stringify(st);
    if (only && prev && !this._style.backgroundColor && this._runStyleKey === key) {
      const r = L.lines[0].runs[0];
      this.setSize(L.width, L.height);
      prev.setText(r.text).setPosition(-L.width * this._origin.x + r.x, -L.height * this._origin.y + L.lineHeight / 2 - 2);
      this._syncHit();
      return;
    }
    this._runStyleKey = key;
    this.removeAll(true);
    // backgroundColor + padding (Text's box behind the words) become one rectangle behind all runs.
    const pad = this._style.backgroundColor ? padOf(this._style.padding) : { x: 0, y: 0 };
    const bw = L.width + 2 * pad.x, bh = L.height + 2 * pad.y;
    this.setSize(bw, bh);
    const sf = this._sf;
    const ox = -bw * this._origin.x + pad.x, oy = -bh * this._origin.y + pad.y;
    if (this._style.backgroundColor) {
      const { color, alpha } = cssColor(this._style.backgroundColor);
      const bg = this.scene.make.graphics({ x: 0, y: 0, add: false });
      bg.fillStyle(color, alpha).fillRect(ox - pad.x, oy - pad.y, bw, bh);
      if (sf) bg.setScrollFactor(sf[0], sf[1]);
      this.add(bg);
    }
    const tex = this.scene.textures;
    for (const line of L.lines) {
      const cy = oy + line.y + L.lineHeight / 2 - 2;
      for (const r of line.runs) {
        if (r.type === 'text') {
          const t = this.scene.make.text({ x: ox + r.x, y: cy, text: r.text, style: st, add: false }).setOrigin(0, 0.5);
          if (sf) t.setScrollFactor(sf[0], sf[1]);
          this.add(t);
        } else {
          const frame = iconKey(r.name);
          const has = tex.exists('icons') && tex.get('icons').has(frame);
          const img = has ? this.scene.make.image({ x: 0, y: 0, key: 'icons', frame, add: false })
            : this.scene.make.image({ x: 0, y: 0, key: missingTexture(this.scene), add: false });
          const k = r.w / Math.max(img.width, img.height);
          img.setScale(k).setPosition(ox + r.x + r.w / 2, cy);
          if (sf) img.setScrollFactor(sf[0], sf[1]);
          this.add(img);
        }
      }
    }
    this._syncHit();
  }

  // Clickable like a Text: the hit rectangle covers the drawn block and follows every relayout
  // (a Text used as a button — "← Wróć", room exits — keeps working as an IconText).
  setInteractive(config = {}) {
    const a = hitArea(this.width, this.height, this._origin);
    super.setInteractive(new Phaser.Geom.Rectangle(a.x, a.y, a.w, a.h), Phaser.Geom.Rectangle.Contains);
    if (config.useHandCursor && this.input) this.input.cursor = 'pointer';
    return this;
  }

  _syncHit() {
    if (!this.input?.hitArea) return;
    const a = hitArea(this.width, this.height, this._origin);
    this.input.hitArea.setTo(a.x, a.y, a.w, a.h);
  }

}

// Width of a run as Phaser's Text would size it (GetTextSize: measureText + strokeThickness),
// from one shared 2D context instead of a hidden Text per label (no canvas redraw per measure).
let measureCtx = null;
function measurer(st) {
  measureCtx ??= document.createElement('canvas').getContext('2d');
  const font = `${st.fontStyle ?? ''} ${st.fontSize ?? '18px'} ${st.fontFamily ?? 'system-ui'}`.trim();
  const stroke = st.strokeThickness ?? 0;
  return (text) => { measureCtx.font = font; return Math.ceil(measureCtx.measureText(text).width + stroke); };
}

function padOf(p) {
  if (p == null) return { x: 0, y: 0 };
  if (typeof p === 'number') return { x: p, y: p };
  return { x: p.x ?? p.left ?? 0, y: p.y ?? p.top ?? 0 };
}

// '#rgb' / '#rrggbb' / '#rrggbbaa' → { color, alpha } for Graphics.
function cssColor(c) {
  let h = String(c).replace('#', '');
  if (h.length === 3) h = [...h].map((x) => x + x).join('');
  const color = parseInt(h.slice(0, 6), 16) || 0;
  const alpha = h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { color, alpha };
}

function missingTexture(scene) {                        // a neutral dot until the art exists
  if (!scene.textures.exists('icon__missing')) {
    const g = scene.make.graphics({ x: 0, y: 0, add: false });
    g.fillStyle(0x8d5a2b, 1); g.fillCircle(24, 24, 22); g.fillStyle(0xfff3dc, 1); g.fillCircle(24, 24, 12);
    g.generateTexture('icon__missing', 48, 48); g.destroy();
  }
  return 'icon__missing';
}

export function iconText(scene, x, y, text, style) { return new IconText(scene, x, y, text, style); }
